import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchEntryBySlug, fetchCollection } from '../lib/cms';
import ScrollReveal from '../components/ScrollReveal';

export default function ViTilbyder() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchEntryBySlug('da', 'vi-tilbyder');
        const allGalleries = await fetchCollection('galleries');
        setPageData(page);
        setGalleries(allGalleries);
      } catch (err) {
        console.error('Error loading vi-tilbyder data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || pageData?.meta_title || "Vi tilbyder | Tømrer- & murerarbejde i Galten & omegn | SKT Byg";
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description || "Se hvad SKT Byg tilbyder: Nybyggeri, tilbygning, renovering, tagarbejde, døre, vinduer, murerarbejde og Junckers gulve i Galten, Silkeborg og Aarhus.";

  const pageTitle = pageData?.page_title?.da || pageData?.page_title || "Vi tilbyder";
  const pageSubtitle = pageData?.page_subtitle?.da || pageData?.page_subtitle || "Vi sikrer dig kvalitet og godt håndværk! Godt håndværk er altid vores drivkraft! Vi gør meget ud af sammen med kunden at finde frem til den bedste løsning.";
  const qualityListTitle = pageData?.quality_title?.da || pageData?.quality_title || "Vi har gode erfaringer med:";

  const carouselImages = (pageData?.section_images && pageData.section_images.length > 0)
    ? pageData.section_images
    : [
        "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/carousel-1.jpg",
        "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/carousel-2.jpg",
        "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/carousel-3.jpg",
        "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/carousel-4.jpg"
      ];

  return (
    <div className="min-h-screen bg-brand-light py-16 overflow-x-hidden">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/junckers-logo.jpg" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Sidetitel */}
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" direction="up">
          <span className="text-xs font-bold text-brand-accent uppercase tracking-widest mb-2 block">
            Hvad vi kan hjælpe med
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-4 uppercase">
            {pageTitle}
          </h1>
          <p className="text-lg text-brand-dark/70 leading-relaxed">
            {pageSubtitle}
          </p>
        </ScrollReveal>

        {/* Karusel Billeder Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {carouselImages.map((img, idx) => (
            <ScrollReveal key={idx} delay={idx * 100} direction="up">
              <div className="relative h-64 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <img
                  src={img}
                  alt={`Reference ${idx + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Kompetencer & Erfaringer */}
        <ScrollReveal direction="up" delay={100}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Tømrerarbejde */}
            <div className="bg-white border border-brand-sand p-8 md:p-10 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded bg-brand-dark text-white flex items-center justify-center">
                    <i className="fa-solid fa-hammer text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-brand-accent uppercase tracking-widest block">Fagområde</span>
                    <h2 className="text-2xl font-extrabold text-brand-dark uppercase">Tømrerarbejde</h2>
                  </div>
                </div>
                <p className="text-brand-dark/80 text-sm leading-relaxed mb-6">
                  Vi udfører alle typer opgaver inden for tømrer- og snedkerfaget med fokus på præcision og kvalitet.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "Nybygninger og tilbygninger",
                    "Døre og vinduer samt tagarbejde",
                    "Terrasser, carporte og udestuer",
                    "Akustikberegninger & snedkerarbejde",
                    "Isolering og energivejledning"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2.5 text-sm font-semibold text-brand-dark/80">
                      <i className="fa-solid fa-check text-brand-accent text-xs shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/vi-tilbyder/nybyggeri"
                className="inline-flex items-center gap-2 text-xs font-bold text-brand-dark hover:text-brand-accent uppercase tracking-wider pt-4 border-t border-brand-sand transition-colors group"
              >
                <span>Udforsk tømreropgaver</span>
                <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            {/* Murerafdeling */}
            <div className="bg-white border border-brand-sand p-8 md:p-10 shadow-sm flex flex-col justify-between border-t-4 border-t-brand-accent">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded bg-brand-accent text-white flex items-center justify-center">
                    <i className="fa-solid fa-trowel-bricks text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-brand-accent uppercase tracking-widest block">Egen afdeling</span>
                    <h2 className="text-2xl font-extrabold text-brand-dark uppercase">Murerafdeling</h2>
                  </div>
                </div>
                <p className="text-brand-dark/80 text-sm leading-relaxed mb-6">
                  Vores faglærte murersvende udfører kvalitetsmurerarbejde til både private og erhvervsprojekter.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    "Opmuring ved nybyggeri og tilbygninger",
                    "Badeværelsesrenovering og flisearbejde",
                    "Facaderenovering, omfugning og vandskuring",
                    "Sokkelarbejde og støbeopgaver",
                    "Tæt koordinering med tømrerafdelingen"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2.5 text-sm font-semibold text-brand-dark/80">
                      <i className="fa-solid fa-check text-brand-accent text-xs shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/vi-tilbyder/murerafdeling"
                className="inline-flex items-center gap-2 text-xs font-bold text-brand-accent hover:text-brand-dark uppercase tracking-wider pt-4 border-t border-brand-sand transition-colors group"
              >
                <span>Læs mere om Murerafdelingen</span>
                <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </ScrollReveal>

        {/* Samlet oversigt */}
        <ScrollReveal direction="up" delay={100}>
          <div className="bg-white border border-brand-sand p-8 md:p-12 shadow-sm mb-16">
            <div className="w-full">
              <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark mb-6 uppercase">
                Vi har gode erfaringer med:
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-4">
                {[
                  "Nybygninger og tilbygninger for private og erhverv",
                  "Små og store renoveringsopgaver",
                  "Døre og vinduer samt tage og kviste",
                  "Autoriseret glarmesterarbejde & termoruder",
                  "Udbedring af forsikringsskader (storm, vand & brand)",
                  "VELUX certificeret montering af tagvinduer",
                  "Murerafdeling med opmuring, fliser og facader",
                  "Certificeret Junckers gulvlægger",
                  "Akustikberegninger & lydregulering",
                  "Isolering og energivejledning",
                  "Forefaldent tømrerarbejde i kirker",
                  "Individuelle snedkerløsninger – fx butiksinventar som diske, lysvægge, reoler",
                  "Gennemgang og rådgivning af bolig inden huskøb",
                  "Hustømrer/vicevært for virksomheder inden for detail, montage og it-området",
                  "Online timeregistrering med præcise specifikationer"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm font-semibold text-brand-dark/80 leading-relaxed hover:translate-x-1 transition-transform duration-200">
                    <i className="fa-solid fa-check text-brand-accent mt-1 shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ScrollReveal>

        {/* Certificeret Junckers Gulvlægger */}
        <ScrollReveal direction="up" delay={150}>
          <div id="junckers" className="scroll-mt-32 bg-brand-sand p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-4 flex justify-center">
              <img
                src="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/junckers-logo.jpg"
                alt="Junckers Logo"
                className="max-h-32 w-auto object-contain shadow-sm"
              />
            </div>
            <div className="lg:col-span-8 space-y-6">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest block">
                Certificeret Gulvlægger
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark uppercase">
                Certificeret Junckers Gulvlægger
              </h2>
              <p className="text-brand-dark/80 leading-relaxed">
                Vælger du et Junckers gulv, er du sikker på at få et gulv der holder i mange år. Som certificeret Junckers gulvlægger, får du masser af fordele, når vi lægger dit gulv.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Udvidet garanti fra 2 til 20 år",
                  "Råd og vejledning af gulvlæggeren",
                  "Gulvet bliver installeret korrekt",
                  "Kun gulve af højeste kvalitet"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-sm font-bold text-brand-dark/90 hover:translate-x-1 transition-transform duration-200">
                    <i className="fa-solid fa-circle-check text-brand-accent"></i>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/junckers-gulve.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-brand-dark hover:bg-brand-accent text-white font-bold shadow-md transition-colors duration-300 flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-file-pdf"></i>
                  <span>Se vores PDF om Junckers Gulve</span>
                </a>
                <a
                  href="http://www.junckers.dk/kampagne/junckers-certificerede-gulvlaegger/flemming-juncker-first-cut-quality.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white hover:bg-brand-sand-dark text-brand-dark border border-brand-sand-dark font-bold transition-colors duration-300 cursor-pointer"
                >
                  Læs om Junckers kvalitet
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* VELUX Certificeret Dagslys & Tagvinduer */}
        <ScrollReveal direction="up" delay={150}>
          <div id="velux" className="scroll-mt-32 bg-brand-dark text-white p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16 shadow-md border border-white/10">
            <div className="lg:col-span-8 space-y-6">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest block">
                Autoriseret montering
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold uppercase">
                VELUX Certificeret Dagslysrådgiver & Montør
              </h2>
              <p className="text-white/80 leading-relaxed">
                Vi er VELUX certificeret, hvilket betyder, at vi har den nyeste viden om korrekt montering, isoleringskraver og dagslysoptimering. Få optimalt lysindfald og et sundt indeklima med professionelt monterede VELUX ovenlysvinduer, lystunneler og fladtagsvinduer.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Korrekt og tæt montering efter VELUX forskrifter",
                  "Dagslysrådgivning og energioptimering",
                  "Udskiftning af ældre tagvinduer uden indvendig skade",
                  "Montering af gardiner, solafskærmning og styring"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-sm font-bold text-white/90">
                    <i className="fa-solid fa-circle-check text-brand-accent"></i>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-brand-accent hover:bg-brand-accent-hover text-white font-bold shadow-md transition-colors duration-300 uppercase text-xs tracking-wider"
                >
                  <span>Få tilbud på VELUX ovenlysvinduer</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 flex justify-center">
              <div className="bg-white/10 p-8 border border-white/20 text-center backdrop-blur-sm max-w-xs w-full">
                <div className="w-16 h-16 bg-brand-accent text-white flex items-center justify-center text-3xl mx-auto mb-4">
                  <i className="fa-solid fa-sun"></i>
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-2">VELUX Certificeret</h3>
                <p className="text-xs text-white/70">Kvalitetsgodkendt montering og rådgivning i tag- og ovenlysvinduer.</p>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* EG Webtid */}
        <ScrollReveal direction="up" delay={150}>
          <div id="webtid" className="scroll-mt-32 bg-white border border-brand-sand p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-8 space-y-6">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest block">
                Effektiv styring
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark uppercase">
                Time- og materialeregistrering med EG Webtid:
              </h2>
              <p className="text-brand-dark/80 leading-relaxed">
                Med EG Webtid sikrer vi en professionel og gennemskuelig proces for alle vores kunder. Det giver os et langt bedre overblik over alle timer og materialer, så vi kan fakturere hurtigt og præcist.
              </p>
              <ul className="space-y-3">
                {[
                  "Har vi et langt bedre overblik over alle timer – og det hele samlet ét sted.",
                  "Slipper vi for rodede papirlapper i forbindelse med arbejdet.",
                  "Kan vores medarbejdere selv registrere timer og materialer direkte på mobilen eller computeren.",
                  "Går fakturering og registrering meget hurtigere."
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm font-semibold text-brand-dark/80 leading-relaxed hover:translate-x-1 transition-transform duration-200">
                    <i className="fa-solid fa-circle-check text-brand-accent mt-1 shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4">
                <a
                  href="https://www.youtube.com/watch?v=9R8kM6NyORY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-brand-accent hover:bg-brand-accent-hover text-white font-bold shadow-md transition-colors duration-300 cursor-pointer"
                >
                  <i className="fa-brands fa-youtube text-lg"></i>
                  <span>Se video om EG Webtid på YouTube</span>
                </a>
              </div>
            </div>
            <div className="lg:col-span-4 flex justify-center">
              <img
                src="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/kimkold-300.png"
                alt="EG Webtid"
                className="max-h-64 w-auto object-contain shadow-sm"
              />
            </div>
          </div>
        </ScrollReveal>

        {/* Dynamiske Gallerier tilknyttet ydelser */}
        <div>
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-12" direction="up">
            <h2 className="text-3xl font-extrabold text-brand-dark uppercase">
              Se vores referencegallerier
            </h2>
            <p className="text-brand-dark/70 mt-2">
              Klik på et galleri nedenfor for at se billeder af vores udførte projekter inden for de enkelte ydelseskategorier.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleries.map((gallery, idx) => (
              <ScrollReveal key={gallery.id} delay={idx * 100} direction="up">
                <Link
                  to={`/referencegalleri/${gallery.slug.da}`}
                  className="group bg-white border border-brand-sand overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col cursor-pointer h-full"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={gallery.images?.[0] || "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/no-image.png"}
                      alt={gallery.title.da}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex-grow flex flex-col justify-between">
                    <h3 className="font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300">
                      {gallery.title.da}
                    </h3>
                    <span className="text-xs font-bold text-brand-accent flex items-center gap-1 mt-3">
                      Se galleri <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform duration-300"></i>
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
